import React, { useState, useRef, useEffect, useCallback } from 'react';
import { countries, getCitiesForCountry } from '../data/countryCityData';

/**
 * Typeahead/autocomplete input — renders a text field that filters a list
 * of options as the user types.  Clicking an option (or pressing Enter on
 * a highlighted one) selects it.  The dropdown closes on blur / Escape.
 *
 * Styled to match the existing Tailwind dark-themed inputs used throughout
 * the app (bg-gray-800, border-gray-600, emerald focus ring, etc.).
 */
const TypeaheadInput = ({ id, value, onChange, options, placeholder, className, disabled, maxLength }) => {
    const [open, setOpen] = useState(false);
    const [highlightIdx, setHighlightIdx] = useState(-1);
    const wrapperRef = useRef(null);
    const listRef = useRef(null);
    const inputRef = useRef(null);

    const filtered = options.filter(o =>
        o.toLowerCase().includes((value || '').toLowerCase())
    );

    // Close dropdown when clicking outside
    useEffect(() => {
        const handler = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Scroll highlighted item into view
    useEffect(() => {
        if (listRef.current && highlightIdx >= 0) {
            const el = listRef.current.children[highlightIdx];
            if (el) el.scrollIntoView({ block: 'nearest' });
        }
    }, [highlightIdx]);

    const select = useCallback((val) => {
        onChange(val);
        setOpen(false);
        setHighlightIdx(-1);
    }, [onChange]);

    const handleKeyDown = (e) => {
        if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            setOpen(true);
            return;
        }

        if (!open) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightIdx(prev => Math.min(prev + 1, filtered.length - 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightIdx(prev => Math.max(prev - 1, 0));
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightIdx >= 0 && highlightIdx < filtered.length) {
                    select(filtered[highlightIdx]);
                }
                break;
            case 'Escape':
                setOpen(false);
                setHighlightIdx(-1);
                break;
            default:
                break;
        }
    };

    return (
        <div ref={wrapperRef} className="relative">
            <input
                ref={inputRef}
                id={id}
                type="text"
                value={value || ''}
                onChange={e => {
                    onChange(e.target.value);
                    setOpen(true);
                    setHighlightIdx(-1);
                }}
                onFocus={() => setOpen(true)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                className={className}
                disabled={disabled}
                maxLength={maxLength}
                autoComplete="off"
            />
            {open && filtered.length > 0 && (
                <ul
                    ref={listRef}
                    className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto bg-gray-800 border border-gray-600 rounded-lg shadow-lg"
                    role="listbox"
                >
                    {filtered.slice(0, 100).map((opt, idx) => (
                        <li
                            key={opt}
                            role="option"
                            aria-selected={idx === highlightIdx}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                select(opt);
                            }}
                            onMouseEnter={() => setHighlightIdx(idx)}
                            className={`px-3 py-2 cursor-pointer text-sm ${
                                idx === highlightIdx
                                    ? 'bg-emerald-700 text-white'
                                    : 'text-gray-200 hover:bg-gray-700'
                            }`}
                        >
                            {opt}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};


/**
 * CountryCitySelect
 *
 * Renders a country dropdown and a city typeahead autocomplete.
 * When the country changes the city list updates automatically.
 *
 * Props:
 *  - country       {string}   current country value
 *  - city          {string}   current city value
 *  - onCountryChange {fn}     called with new country string
 *  - onCityChange    {fn}     called with new city string
 *  - countryLabel  {string}   label text for the country field  (default "Country")
 *  - cityLabel     {string}   label text for the city field     (default "City / Town")
 *  - cityRequired  {boolean}  whether the city field is required (adds *)
 *  - countryRequired {boolean} whether the country field is required (adds *)
 *  - fieldClass    {string}   CSS class for inputs
 *  - labelClass    {string}   CSS class for labels
 *  - layout        {"row"|"stacked"} how to lay out the two fields (default "row")
 *  - defaultCountry {string}  initial country if nothing set   (default "United Kingdom")
 *  - disabled      {boolean}  disable both fields
 */
const CountryCitySelect = ({
    country,
    city,
    onCountryChange,
    onCityChange,
    countryLabel = 'Country',
    cityLabel = 'City / Town',
    cityRequired = false,
    countryRequired = false,
    fieldClass = 'w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm',
    labelClass = 'block text-sm font-medium text-gray-300 mb-1',
    layout = 'row',
    defaultCountry = 'United Kingdom',
    disabled = false,
}) => {
    // If no country is set yet, pre-fill with default
    useEffect(() => {
        if (!country && defaultCountry && onCountryChange) {
            onCountryChange(defaultCountry);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const cityOptions = getCitiesForCountry(country || defaultCountry);

    const handleCountryChange = (e) => {
        const newCountry = e.target.value;
        onCountryChange(newCountry);
        // Clear city when country changes (city list is different)
        if (onCityChange) onCityChange('');
    };

    const containerClass = layout === 'row'
        ? 'grid grid-cols-1 sm:grid-cols-2 gap-4'
        : 'space-y-4';

    return (
        <div className={containerClass}>
            {/* Country dropdown */}
            <div>
                <label className={labelClass}>
                    {countryLabel} {countryRequired && <span className="text-red-400">*</span>}
                </label>
                <select
                    value={country || ''}
                    onChange={handleCountryChange}
                    className={fieldClass}
                    disabled={disabled}
                >
                    <option value="">Select a country</option>
                    {countries.map(c => (
                        <option key={c} value={c}>{c}</option>
                    ))}
                </select>
            </div>

            {/* City autocomplete */}
            <div>
                <label className={labelClass}>
                    {cityLabel} {cityRequired && <span className="text-red-400">*</span>}
                </label>
                <TypeaheadInput
                    value={city || ''}
                    onChange={onCityChange}
                    options={cityOptions}
                    placeholder="Start typing a city…"
                    className={fieldClass}
                    disabled={disabled || !country}
                    maxLength={100}
                />
            </div>
        </div>
    );
};

export { TypeaheadInput };
export default CountryCitySelect;
